import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import isEmpty from "lodash/isEmpty";
import FormContainer from "@ats/src/components/forms/FormContainer";
import FormInput from "@ats/src/components/forms/FormInput";
import FormTextarea from "@ats/src/components/forms/FormTextarea";
import FormUploader from "@ats/src/components/forms/FormUploader";
import Button from "@ats/src/components/shared/Button";

import { removeHttpPrefix, removeKnownUrlPrefix } from "@shared/lib/utils";

import QuestionResponse from "../components/QuestionResponse";

import styled from "@emotion/styled";
import { css } from "@emotion/react";

function ApplicationForm(props) {
  const {
    questions,
    errors,
    questionResponsesState,
    handleQuestionResponseChange,
    recaptchaRef,
  } = props;

  const onSubmit = (event) => {
    event.preventDefault();
    props.onSubmit();
  };

  const customQuestionNodes = (questions) => {
    if (!questions || questions.length === 0) return null;

    return questions.map(
      (question, index) => (
        <QuestionResponse
          key={question.id}
          index={index}
          question={question}
          questionResponse={questionResponsesState[question.id]}
          errors={props.errors}
          handleQuestionResponseChange={handleQuestionResponseChange}
          clearFixedErrors={clearFixedErrors}
        />
      ),
      // undefined,
    );
  };

  const onInvalidFile = (file, expectedFileType) => {
    props.onInputChange({
      resumeFile: undefined,
      errors: { resume: [window.APP_TRANSLATIONS.resume_error] },
    });
  };

  const inputs = () => {
    const {
      errors,
      name,
      email,
      phone,
      location,
      linkedinUrl,
      githubUrl,
      twitterUrl,
      dribbbleUrl,
      websiteUrl,
      resumeFile,
      settings,
    } = props;

    if (settings === undefined) return [];

    return [
      {
        name: "name",
        label: window.APP_TRANSLATIONS.name_label,
        placeholder: "",
        value: name,
        errors: errors,
        requirementSetting: settings["name"],
        type: "text",
        component: "FormInput",
        autoComplete: "name",
        autoCapitalize: "words",
      },
      {
        name: "email",
        label: window.APP_TRANSLATIONS.email_address_label,
        placeholder: "",
        value: email,
        errors: errors,
        requirementSetting: settings["email"],
        type: "email",
        component: "FormInput",
        autoComplete: "email",
      },
      {
        name: "phone",
        label: window.APP_TRANSLATIONS.phone_number_label,
        placeholder: "",
        value: phone,
        errors: errors,
        requirementSetting: settings["phone"],
        type: "text",
        component: "FormInput",
        autoComplete: "tel",
      },
      {
        name: "location",
        label: window.APP_TRANSLATIONS.location_label,
        placeholder: window.APP_TRANSLATIONS.location_placeholder,
        value: location,
        errors: errors,
        requirementSetting: settings["location"],
        type: "text",
        component: "FormInput",
        autoComplete: "address-level2",
      },
      {
        name: "linkedinUrl",
        label: window.APP_TRANSLATIONS.linkedin_label,
        placeholder: "...",
        value: removeKnownUrlPrefix(linkedinUrl) || "",
        errors: errors,
        requirementSetting: settings["linkedinUrl"],
        type: "text",
        pretext: "https://linkedin.com/in/",
        component: "FormInput",
      },
      {
        name: "twitterUrl",
        label: window.APP_TRANSLATIONS.twitter_label,
        placeholder: "...",
        value: removeKnownUrlPrefix(twitterUrl) || "",
        errors: errors,
        requirementSetting: settings["twitterUrl"],
        type: "text",
        pretext: "https://twitter.com/",
        component: "FormInput",
      },
      {
        name: "githubUrl",
        label: window.APP_TRANSLATIONS.github_label,
        placeholder: "...",
        value: removeKnownUrlPrefix(githubUrl) || "",
        errors: errors,
        requirementSetting: settings["githubUrl"],
        type: "text",
        pretext: "https://github.com/",
        component: "FormInput",
      },
      {
        name: "dribbbleUrl",
        label: window.APP_TRANSLATIONS.dribbble_label,
        placeholder: "...",
        value: removeKnownUrlPrefix(dribbbleUrl) || "",
        errors: errors,
        requirementSetting: settings["dribbbleUrl"],
        type: "text",
        pretext: "https://dribbble.com/",
        component: "FormInput",
      },
      {
        name: "websiteUrl",
        label: window.APP_TRANSLATIONS.website_label,
        placeholder: "...",
        value: removeHttpPrefix(websiteUrl) || "",
        errors: errors,
        requirementSetting: settings["websiteUrl"],
        type: "text",
        pretext: "https://",
        component: "FormInput",
      },
      {
        name: "resumeFile",
        label: window.APP_TRANSLATIONS.resume_label,
        placeholder: window.APP_TRANSLATIONS.resume_placeholder,
        value: resumeFile,
        errors: errors,
        requirementSetting: settings["resume"],
        type: "file",
        component: "FileUpload",
      },
    ];
  };

  const recaptchaElement = (
    <Styled.RecaptchaContainer>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={(window as any).GOOGLE_RECAPTCHA_SITE_KEY}
        className="grecaptcha-badge"
      />
    </Styled.RecaptchaContainer>
  );

  const standardQuestions = () => {
    const inputsArr = inputs();

    return inputsArr.map((input) => {
      const {
        name,
        placeholder,
        label,
        value,
        type,
        pretext,
        component,
        requirementSetting,
        errors,
        autoComplete,
        autoCapitalize,
      } = input;

      if (requirementSetting === "hidden") return null;

      if (component === "FormInput")
        return (
          <FormInput
            key={name}
            onChange={handleInputChange}
            name={name}
            type={type}
            label={label}
            isRequired={requirementSetting === "required"}
            requiredLabel={window.APP_TRANSLATIONS.required}
            placeholder={placeholder}
            value={value}
            errors={errors}
            pretext={pretext}
            autoComplete={autoComplete}
            autoCapitalize={autoCapitalize}
          />
        );

      if (component === "FormTextarea")
        return (
          <FormTextarea
            key={name}
            onChange={handleInputChange}
            name={name}
            inputType={type}
            label={label}
            isRequired={requirementSetting === "required"}
            requiredLabel={window.APP_TRANSLATIONS.required}
            placeholder={placeholder}
            value={value}
            errors={errors}
            rows={8}
          />
        );

      if (component === "FileUpload") {
        return (
          <span key={name}>
            <FormUploader
              errors={errors}
              onChange={handleFileInputChange}
              name={name}
              label={label}
              file={value}
              placeholder={placeholder}
              isRequired={requirementSetting === "required"}
              requiredLabel={window.APP_TRANSLATIONS.required}
              expectedFileType="document"
              allowAutomaticUploading={false}
              fileIsAlreadyUploaded={false}
              onInvalidFile={onInvalidFile}
              loading={false}
              onCompleteDirectUpload={() => undefined}
            />
          </span>
        );
      }
    });
  };

  const clearFixedErrors = (fieldName, errors) => {
    // window.logger("%c[ApplicationForm] clearFixedErrors", "color: #1976D2", {
    //   fieldName,
    //   errors,
    //   props,
    // });

    if (errors != undefined && errors[fieldName]) {
      // Mark error as fixed for this field
      delete errors[fieldName];
    }
    if (isEmpty(errors)) {
      errors = undefined;
    }
    // console.log("%c[ApplicationForm] clearFixedErrors", "color: #1976D2", { errors });
    return errors;
  };

  const handleInputChange = (name, value) => {
    // console.warn('[ApplicationForm] handleInputChange name', name);
    let val = value;
    const errors = clearFixedErrors(name, props.errors);

    if (name === "linkedinUrl") {
      val = removeKnownUrlPrefix(val);
    } else if (name === "websiteUrl") {
      val = removeHttpPrefix(val);
    }

    props.onInputChange({ [name]: val, errors });
  };

  /* Handle File Input Change
  --===================================================-- */
  const handleFileInputChange = ({ fileToUpload }) => {
    console.log("[ApplicationForm] handleFileInputChange fileToUpload", fileToUpload);

    if (fileToUpload) {
      const errors = clearFixedErrors("resume", props.errors);
      props.onInputChange({ resumeFile: fileToUpload, errors });
    }
  };

  const submitButton = (
    <Styled.Button onClick={onSubmit} disabled={props.submitLoading} loading={props.submitLoading}>
      {window.APP_TRANSLATIONS.submit_application}
    </Styled.Button>
  );

  return (
    <>
      <Styled.Title>{window.APP_TRANSLATIONS.apply_here}</Styled.Title>
      <FormContainer errors={errors} buttons={submitButton}>
        {standardQuestions()}
        {customQuestionNodes(questions)}
        {recaptchaElement}
      </FormContainer>
    </>
  );
}

export default ApplicationForm;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.Title = styled.h2((props: any) => {
  const t: any = props.theme;
  return css`
    label: ApplicationForm_Title;
    ${[t.mb(6), t.mt(1), t.text.h2, t.text.bold]}
    color: ${t.color.black};
  `;
});

Styled.Button = styled(Button)((props) => {
  const t: any = props.theme;
  return css`
    label: ApplicationForm_Button;
    ${t.mt(2)}
  `;
});

Styled.RecaptchaContainer = styled.div`
  label: ApplicationForm_RecaptchaContainer;

  .grecaptcha-badge {
    visibility: hidden;
  }
`;
